import { useState } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/utils/cn'

interface BoostRefundDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (reason: string) => void;
  isPending: boolean;
}

const MIN_REASON_LENGTH = 10;

export function BoostRefundDialog({
  open,
  onOpenChange,
  onSubmit,
  isPending,
}: BoostRefundDialogProps) {
  const [reason, setReason] = useState('');

  const canSubmit = reason.trim().length >= MIN_REASON_LENGTH && !isPending;

  const handleSubmit = () => {
    if (canSubmit) {
      onSubmit(reason.trim());
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setReason('');
    }
    onOpenChange(nextOpen);
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild>
              <motion.div
                className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content asChild>
              <motion.div
                className={cn(
                  "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg",
                  "bg-dark-900 backdrop-blur-xl border border-dark-700 rounded-xl shadow-2xl",
                )}
                initial={{ opacity: 0, scale: 0.95, x: "-50%", y: "-48%" }}
                animate={{ opacity: 1, scale: 1, x: "-50%", y: "-50%" }}
                exit={{ opacity: 0, scale: 0.95, x: "-50%", y: "-48%" }}
                transition={{ type: "spring", duration: 0.3 }}
              >
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-1">
                      <DialogPrimitive.Title className="text-lg font-semibold mb-2">
                        Request a Refund
                      </DialogPrimitive.Title>
                      <DialogPrimitive.Description className="text-sm text-muted-foreground">
                        Refund requests are reviewed individually by the AI Edge team, typically within 3 business days.
                        Your request will be evaluated in accordance with our{' '}
                        <a
                          href={`${import.meta.env.VITE_AIEW_URL || "https://ai-edge.io"}/policies/add-on-refund`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-muted-foreground underline underline-offset-4 hover:text-foreground transition-colors"
                        >
                          refund policy
                        </a>.
                      </DialogPrimitive.Description>
                    </div>
                    <DialogPrimitive.Close asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <X className="h-4 w-4" />
                      </Button>
                    </DialogPrimitive.Close>
                  </div>

                  <div className="mt-4">
                    <label htmlFor="refund-reason" className="text-sm font-medium text-foreground block mb-2">
                      Reason for your request
                    </label>
                    <textarea
                      id="refund-reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Please describe why you'd like a refund..."
                      rows={4}
                      maxLength={1000}
                      className={cn(
                        "flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm",
                        "ring-offset-background placeholder:text-muted-foreground",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                        "disabled:cursor-not-allowed disabled:opacity-50",
                        "resize-none",
                      )}
                      disabled={isPending}
                    />
                    {reason.length > 0 && reason.trim().length < MIN_REASON_LENGTH && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Please provide at least {MIN_REASON_LENGTH} characters
                      </p>
                    )}
                  </div>

                  <div className="flex justify-end gap-3 mt-6">
                    <Button
                      variant="outline"
                      onClick={() => handleOpenChange(false)}
                      className="bg-dark-800/50 border-dark-700"
                      disabled={isPending}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="default"
                      onClick={handleSubmit}
                      disabled={!canSubmit}
                      className="bg-gradient-to-r from-brand-blue to-brand-blue-dark"
                    >
                      {isPending ? 'Submitting...' : 'Submit Request'}
                    </Button>
                  </div>
                </div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
